import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { Heart } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import MarketingHeader from "@/components/MarketingHeader";
import MarketingFooter from "@/components/MarketingFooter";
import { signup as backendSignup, login as backendLogin } from "@/integrations/backend/auth";
import { useAuth } from "@/hooks/useAuth";
import { getAuthErrorMessage } from "@/lib/auth-errors";
import { hasTokens } from "@/lib/token-storage";
import frequentHealthIcon from "@/assets/frequent-health-icon.jpeg";

export default function Signup() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const navigate = useNavigate();
  const { user, refetchProfile, loading, sessionChecked } = useAuth();

  // If already logged in and we land on /signup → go to PostLogin
  useEffect(() => {
    if (!sessionChecked && hasTokens()) {
      navigate("/post-login", { replace: true });
      return;
    }
    if (sessionChecked && user) navigate("/post-login", { replace: true });
  }, [user, sessionChecked, navigate]);

  // Don't show the signup form until we know whether the user is already authenticated.
  if (loading || !sessionChecked) {
    return (
      <div className="min-h-screen gradient-dark flex items-center justify-center p-4">
        <div className="text-white text-lg">Checking your session…</div>
      </div>
    );
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!firstName || !lastName || !email || !password) {
      toast.error("Please fill in all fields");
      return;
    }
    if (!acceptedTerms) {
      toast.error("You must accept the Terms and Privacy Policy");
      return;
    }
    if (password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }

    setIsSubmitting(true);
    try {
      await backendSignup(email, password, firstName, lastName);
      // Auto-login for DX convenience
      await backendLogin(email, password);
      // Refetch profile after successful login to update auth state
      await refetchProfile();
      toast.success("Account created!");
      navigate("/post-login", { replace: true });
    } catch (err: unknown) {
      console.error("[signup] error", err);
      toast.error(getAuthErrorMessage("signup", err));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
    <MarketingHeader />
    <div className="min-h-screen gradient-dark flex items-center justify-center p-4 pt-24">
      <Card className="w-full max-w-md border-primary/20 shadow-neon-glow bg-dark-card">
        <CardHeader className="text-center">
          <div className="flex items-center justify-center space-x-2 mb-4">
            <img
              src={frequentHealthIcon}
              alt="Frequent Health logo"
              className="h-12 w-auto"
            />
          </div>
          <CardTitle className="text-2xl text-white">Create Your Account</CardTitle>
          <p className="text-gray-light">Start your fitness journey today</p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="firstName" className="text-gray-light">First Name</Label>
                <Input
                  id="firstName"
                  type="text"
                  placeholder="John"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  required
                  disabled={isSubmitting}
                  className="bg-dark-slate border-primary/20 text-white placeholder:text-gray-mid"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="lastName" className="text-gray-light">Last Name</Label>
                <Input
                  id="lastName"
                  type="text"
                  placeholder="Doe"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  required
                  disabled={isSubmitting}
                  className="bg-dark-slate border-primary/20 text-white placeholder:text-gray-mid"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="email" className="text-gray-light">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={isSubmitting}
                className="bg-dark-slate border-primary/20 text-white placeholder:text-gray-mid"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-gray-light">Password</Label>
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={isSubmitting}
                className="bg-dark-slate border-primary/20 text-white placeholder:text-gray-mid"
              />
            </div>

            <div className="flex items-start gap-3 pt-2">
              <Checkbox id="accept" checked={acceptedTerms} onCheckedChange={(v) => setAcceptedTerms(Boolean(v))} />
              <Label htmlFor="accept" className="text-gray-light">
                I agree to the
                {" "}
                <a href="/terms" className="text-primary hover:text-lime-hover underline">Terms of Service</a>
                {" "}and{" "}
                <a href="/privacy" className="text-primary hover:text-lime-hover underline">Privacy Policy</a>.
              </Label>
            </div>

            <Button
              type="submit"
              disabled={isSubmitting}
              className="w-full font-bold uppercase tracking-wider shadow-neon-glow hover:scale-105 transition-all"
            >
              {isSubmitting ? "Creating Account..." : "Sign Up"}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-gray-light text-sm">
              Already have an account?{" "}
              <Link to="/login" className="text-primary hover:text-lime-hover font-medium">
                Sign In
              </Link>
            </p>
          </div>

          <div className="mt-4 text-center">
            <Link to="/" className="text-gray-light hover:text-white text-sm">
              ← Back to Home
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
    <MarketingFooter />
    </>
  );
}
